/**
 * Helpers for owner digital-asset downloads (filename + MIME).
 * Prefer ledger assetName/assetType; fall back to Content-Type / URL sniffing.
 */

const MIME_TO_EXT = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/svg+xml': '.svg',
  'audio/mpeg': '.mp3',
  'audio/mp3': '.mp3',
  'audio/wav': '.wav',
  'audio/x-wav': '.wav',
  'audio/ogg': '.ogg',
  'video/mp4': '.mp4',
  'video/webm': '.webm',
  'application/pdf': '.pdf',
  'application/zip': '.zip',
  'text/plain': '.txt',
};

/**
 * @param {string} mime
 * @returns {string} extension including leading dot, or ''
 */
export function extensionFromMime(mime) {
  if (!mime || typeof mime !== 'string') return '';
  const base = mime.split(';')[0].trim().toLowerCase();
  if (MIME_TO_EXT[base]) return MIME_TO_EXT[base];
  if (base.startsWith('image/')) return `.${base.slice(6).replace('+xml', '')}`;
  return '';
}

/**
 * Build a safe download filename with an extension when possible.
 * @param {{ assetName?: string, itemAssetName?: string, assetType?: string, itemAssetType?: string, contentType?: string }} opts
 */
export function buildDownloadFilename(opts = {}) {
  const rawName = String(opts.assetName || opts.itemAssetName || 'download').trim() || 'download';
  // Strip path separators
  const base = rawName.replace(/[/\\?%*:|"<>]/g, '_').replace(/^\.+/, '') || 'download';
  const hasExt = /\.[a-z0-9]{2,8}$/i.test(base);
  if (hasExt) return base;

  const mime = opts.assetType || opts.itemAssetType || opts.contentType || '';
  const ext = extensionFromMime(mime);
  return ext ? `${base}${ext}` : base;
}

/**
 * Trigger a browser file download from an ArrayBuffer/Blob with explicit MIME + name.
 */
export function triggerBlobDownload(data, filename, mimeType) {
  const type = mimeType || 'application/octet-stream';
  const blob =
    data instanceof Blob
      ? (data.type ? data : new Blob([data], { type }))
      : new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Download NFT original via authenticated MiEMarketApi proxy (owner-only).
 * Falls back to direct IPFS gateway fetch only when nftId is missing (legacy).
 *
 * @param {object} params
 * @param {string} params.apiUrl - MiEMarketApi base URL
 * @param {string} params.idToken - Firebase ID token
 * @param {string} [params.nftId]
 * @param {string} [params.ipfsUrl] - trusted gateway URL fallback
 * @param {string} [params.assetName]
 * @param {string} [params.assetType]
 */
export async function downloadOwnedNftAsset({
  apiUrl,
  idToken,
  nftId,
  ipfsUrl,
  assetName,
  assetType,
}) {
  if (nftId && apiUrl && idToken) {
    const res = await fetch(`${apiUrl}/nft/${encodeURIComponent(nftId)}/asset`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        Accept: '*/*',
      },
    });
    if (!res.ok) {
      let message = `Download failed (${res.status})`;
      try {
        const body = await res.json();
        message = body.message || body.error || message;
      } catch (_) {
        /* ignore */
      }
      throw new Error(message);
    }
    const contentType =
      res.headers.get('content-type') || assetType || 'application/octet-stream';
    // Prefer server Content-Disposition filename when present
    let filename = buildDownloadFilename({ assetName, assetType, contentType });
    const disposition = res.headers.get('content-disposition') || '';
    const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
    if (match?.[1]) {
      filename = decodeURIComponent(match[1].replace(/"/g, '').trim());
    }
    const buffer = await res.arrayBuffer();
    triggerBlobDownload(buffer, filename, contentType);
    return;
  }

  // Legacy fallback: public gateway (CID still public — prefer authenticated path)
  if (!ipfsUrl || !String(ipfsUrl).startsWith('https://ipfs.mieros.cloud/ipfs/')) {
    throw new Error('Invalid or missing asset URL');
  }
  const withName = `${ipfsUrl}${ipfsUrl.includes('?') ? '&' : '?'}filename=${encodeURIComponent(
    buildDownloadFilename({ assetName, assetType })
  )}`;
  const res = await fetch(withName);
  if (!res.ok) throw new Error(`IPFS download failed (${res.status})`);
  const contentType =
    assetType || res.headers.get('content-type') || 'application/octet-stream';
  const filename = buildDownloadFilename({ assetName, assetType, contentType });
  const buffer = await res.arrayBuffer();
  triggerBlobDownload(buffer, filename, contentType);
}
